import React from 'react';
import styled from 'styled-components';
import Cell from './cell';
import HeaderCell from './header-cell';
import { defaultMemoize } from 'reselect';
import { sortByColumn, searchMatchItems, TableItem, noop } from './table-utils';

import { ColumnProps, getColumnWidth } from './column';
import VirtualTr from './virtual-tr';

import debounce from 'lodash-es/debounce';
import { TableConfig } from './context/table-context';
import { Checkbox } from '../../atoms/inputs/index';
import { syncProxyHeader, stickySupported } from './table-scroll-sync-utils';
import colors from '../../styles/colors';
import cx from 'classnames';

export interface ControlledDataProps {
  // getters
  visibleColumns?: { [columnKey: string]: boolean };
  selectedRows?: Set<string>;
}
export interface ControlledTableProps extends ControlledDataProps {
  // setters
  setInitialConfig?: (config: TableConfig) => void;
  toggleSelect?: (rowID: string) => void;
  onSortChange?: (sortKey: string, sortAscending: boolean) => void;
  onSearchChange?: (search: string) => void;
  selectAll?: (rowIDs: string[]) => void;
  deselectAll?: () => void;
  initialSearch?: any;
}

export interface TableProps extends ControlledTableProps {
  className?: string;
  selectable?: boolean /* determines whether or not to show checkboxes on the left */;
  searchable: boolean /* determines whether or not to allow searching */;
  items: TableItem[] /* Each item is one row of the table */;
  width: string /* width of the table */;
  height: string /* height of the table */;
  highlight?: (
    row: TableItem,
  ) => boolean /* items that meet this condition will be scrolled to and highlighted */;
  sticky?: boolean /* Whether or not to honor sticky columns */;
  initialSearch?: any /** Shows search result on initial load */;
}

export interface State {
  columns: ColumnProps[];
  search: string;
  sortKey: string;
  sortAscending: boolean;
  scrollTop: number;
}

const CHECKBOX_COLUMN = 'select-checkbox';
const ROW_HEIGHT = 60;
const ROW_PADDING = 3;

export class Table extends React.Component<TableProps, State> {
  sort = defaultMemoize(
    (
      columns: ColumnProps[],
      columnKey: string,
      items: TableItem[],
      sortAscending: boolean,
    ) => sortByColumn(columns, columnKey, items, sortAscending),
  );

  filter = defaultMemoize(
    (columns: ColumnProps[], items: TableItem[], search: string) =>
      searchMatchItems(columns, items, search),
  );

  private tableWrapperEl;
  private proxyTableWrapperEl;

  private highlightScrollDone = false;

  private useProxyHeader = !stickySupported();

  constructor(props: TableProps) {
    super(props);
    const columns = this.columnChildrenToArray();
    this.onSearch = debounce(this.onSearch, 100);
    this.syncProxyHeader = debounce(this.syncProxyHeader, 50);
    this.setVirtualizeScroll = debounce(this.setVirtualizeScroll, 50);

    const sortKey = this.initialSortKey(columns);
    const sortAscending = true;

    this.state = {
      sortAscending,
      sortKey,
      columns,
      search: this.props.initialSearch ? this.props.initialSearch() : '',
      scrollTop: 0,
    };
  }

  assignProxyTableWrapperRef = (el): void => (this.proxyTableWrapperEl = el);
  assignTableWrapperRef = (el): void => {
    this.tableWrapperEl = el;
  };

  // use the column with defaultSort prop or fall back to first visible column
  initialSortKey = (columns: ColumnProps[]): string => {
    const defaultSort = columns.find(column => column.defaultSort);
    if (!defaultSort) {
      throw new Error(
        `No default sort found. Ensure one column has prop defaultSort={true}`,
      );
    }
    return defaultSort.dataKey;
  };

  buildCheckBoxColumn = (): ColumnProps => {
    const cellContent = ({ id }) => {
      const { toggleSelect, selectedRows } = this.props;

      return (
        <Checkbox
          onChange={toggleSelect.bind(null, id)}
          checked={selectedRows && selectedRows.has(id)}
        />
      );
    };

    return {
      cellContent,
      dataKey: CHECKBOX_COLUMN,
      width: 'sm',
      alwaysVisible: true,
      sticky: true,
      sort: false,
      sectionName: 'checkbox',
    };
  };

  // convert our <Column /> children to a an array for easier data manipulation
  columnChildrenToArray = (): ColumnProps[] => {
    const { children, selectable } = this.props;
    let columns = React.Children.map(
      children,
      child => child && (child as any).props,
    ).filter(d => !!d);

    if (selectable) {
      columns = [this.buildCheckBoxColumn(), ...columns];
    }
    return columns;
  };

  onSearch = (search: string): void => {
    const { deselectAll, onSearchChange } = this.props;

    deselectAll && deselectAll();
    onSearchChange && onSearchChange(search);

    this.scrollToTop();
    this.setState({ search });
  };

  onSortChange = (sortKey: string): void => {
    const { onSortChange } = this.props;
    const columnChanged = sortKey !== this.state.sortKey;
    const sortAscending = columnChanged ? true : !this.state.sortAscending;

    onSortChange && onSortChange(sortKey, sortAscending);
    this.scrollToTop();
    this.setState({ sortKey, sortAscending });
  };

  scrollToTop = (): void => {
    this.tableWrapperEl.scrollTop = 0;
  };

  highlightItems = (): void => {
    const { items, highlight } = this.props;
    const { columns, sortAscending, sortKey } = this.state;

    // ensure this only happens once, when the table is ready to scroll
    if (
      !this.tableWrapperEl ||
      this.highlightScrollDone ||
      !items.length ||
      !highlight
    ) {
      return;
    }

    const sorted = this.sort(columns, sortKey, items, sortAscending);
    const index = sorted.findIndex(highlight);
    const scrollTop = index * ROW_HEIGHT;
    this.tableWrapperEl.scrollTop = scrollTop;
    this.highlightScrollDone = true;
  };

  syncProxyHeader = () => {
    // tslint:disable-next-line:no-this-assignment
    const { tableWrapperEl, proxyTableWrapperEl } = this;
    syncProxyHeader({ tableWrapperEl, proxyTableWrapperEl });
  };

  handleMasterCheckToggle = (): void => {
    const { selectAll, deselectAll, items } = this.props;

    const { search } = this.state;

    if (this.allRowsSelected()) {
      deselectAll();
    } else {
      const visible = this.getVisibleColumns();
      const filtered = this.filter(visible, items, search);
      const filteredIDs = filtered.map(i => i.id);

      selectAll(filteredIDs);
    }
  };

  allRowsSelected = (): boolean => {
    const { selectedRows, items } = this.props;
    const { search } = this.state;

    const visible = this.getVisibleColumns();
    const filtered = this.filter(visible, items, search);

    return selectedRows && selectedRows.size === filtered.length;
  };

  // returns the sticky left position of each column
  getColumnOffsets = (): number[] => {
    const columns = this.getVisibleColumns();
    let totalOffset = 0;
    const offsets = [];

    columns.forEach(column => {
      offsets.push(totalOffset);
      totalOffset += getColumnWidth(column);
    });
    return offsets;
  };

  // get the columns that are currently visible
  getVisibleColumns = (): ColumnProps[] => {
    const { columns } = this.state;
    const { visibleColumns } = this.props;
    return columns.filter(c => !!visibleColumns[c.dataKey]);
  };

  getAlwaysSearchableColumns = (): ColumnProps[] => {
    const { columns } = this.state;
    return columns.filter(c => !!c.alwaysSearchable);
  };

  setVirtualizeScroll = (scrollTop: number) => {
    this.setState({ scrollTop });
  };

  // builds a function to determine which rows to show or virtualize
  rowIsVisibleFnGenerator = (count: number): ((number) => boolean) => {
    // Fix for severe-event.test.tsx Jest couldn't create pseudoElement
    if (process.env.JEST_WORKER_ID !== undefined && !this.tableWrapperEl) {
      return noop;
    }

    let rowsOnScreen = 0;

    const rowFullHeight = ROW_HEIGHT + ROW_PADDING;

    if (!this.tableWrapperEl) {
      // Creating pseudoElement if table is not rendered yet, to calculate height
      const pseudoElement = document.createElement('div');
      pseudoElement.style.height = this.props.height;
      document.body.appendChild(pseudoElement);
      rowsOnScreen = Math.ceil(pseudoElement.offsetHeight / rowFullHeight);
      document.body.removeChild(pseudoElement);
    } else {
      rowsOnScreen = Math.ceil(
        this.tableWrapperEl.offsetHeight / rowFullHeight,
      );
    }

    // if using an older browser, overscan fewer elements for performance boost
    const overscanCount = this.useProxyHeader ? 2 : rowsOnScreen;

    const { scrollTop } = this.state;
    const topVisibleIndex = Math.floor(scrollTop / rowFullHeight);

    const minVisibleIndex =
      topVisibleIndex - overscanCount > -1
        ? topVisibleIndex - overscanCount
        : 0;
    const maxVisibleIndex = topVisibleIndex + rowsOnScreen + overscanCount;

    return (index: number) => {
      return index <= maxVisibleIndex && index >= minVisibleIndex;
    };
  };

  onScroll = e => {
    if (e.target === this.tableWrapperEl) {
      if (this.useProxyHeader) {
        this.syncProxyHeader();
      }
      this.setVirtualizeScroll(e.target.scrollTop);
    }
  };

  headerRow = () => {
    const { sortAscending, sortKey, search } = this.state;
    const { selectable, searchable } = this.props;
    const visible = this.getVisibleColumns();
    const offsets = this.getColumnOffsets();

    return (
      <thead>
        <tr className="header-row" data-testid="table-header-row">
          {visible.map((column, columnIndex) => {
            const isMasterCheckbox = column.dataKey === CHECKBOX_COLUMN;

            const onCheckChange = isMasterCheckbox
              ? this.handleMasterCheckToggle
              : undefined;

            // determine if this header cell as a search bar in it
            const searchColumnIndex = selectable ? 1 : 0;
            const isSearchColumn =
              searchable && columnIndex === searchColumnIndex;

            const onSearch = isSearchColumn ? this.onSearch : undefined;

            return (
              <HeaderCell
                {...{
                  key: column.dataKey,
                  column,
                  offset: offsets[columnIndex],
                  // sorting props
                  sortKey,
                  sortAscending,
                  onSort: this.onSortChange,
                  // searching prop
                  onSearch,
                  // master checkbox props
                  checked: this.allRowsSelected(),
                  onCheckChange,
                  value: search,
                }}
              />
            );
          })}
        </tr>
      </thead>
    );
  };

  // This element will appear only in browsers that don't support css sticky (IE11)
  // it is synchronized with the header row in both width and horizontal scroll
  proxyHeaderRow = () => (
    <div className="proxy-table-wrapper" ref={this.assignProxyTableWrapperRef}>
      <table className="proxy-table">{this.headerRow()}</table>
    </div>
  );

  componentDidMount() {
    const { setInitialConfig } = this.props;
    const { columns, search, sortKey, sortAscending } = this.state;
    setInitialConfig &&
      setInitialConfig({ columns, search, sortKey, sortAscending });

    this.highlightItems();

    this.syncProxyHeader();
    if (this.useProxyHeader) {
      window.addEventListener('scroll', this.syncProxyHeader);
      window.addEventListener('resize', this.syncProxyHeader);
    }
  }

  componentWillUnmount() {
    if (this.useProxyHeader) {
      window.removeEventListener('scroll', this.syncProxyHeader);
      window.removeEventListener('resize', this.syncProxyHeader);
    }
  }

  componentDidUpdate(prevProps: TableProps, prevState: State) {
    this.highlightItems();
    this.syncProxyHeader();
  }

  public render() {
    const {
      className,
      items,
      selectedRows,
      visibleColumns,
      highlight,
    } = this.props;

    const { columns, search, sortAscending, sortKey } = this.state;

    if (!visibleColumns) {
      return null;
    }
    const visible = this.getVisibleColumns();
    const alwaysSearchable = this.getAlwaysSearchableColumns();

    let filtered;
    // order and filter the items (these functions are memoized)
    const sorted = this.sort(columns, sortKey, items, sortAscending);
    !alwaysSearchable.length
      ? (filtered = this.filter(visible, sorted, search))
      : (filtered = this.filter(alwaysSearchable, sorted, search));

    const offsets = this.getColumnOffsets();

    const rowIsVisible = this.rowIsVisibleFnGenerator(filtered.length);

    return (
      <div
        className={`table-wrapper ${className}`}
        onScroll={this.onScroll}
        ref={this.assignTableWrapperRef}
        data-testid="table-wrapper"
      >
        {this.useProxyHeader && this.proxyHeaderRow()}
        <table className={cx('table', { 'hide-header': this.useProxyHeader })}>
          {this.headerRow()}
          <tbody>
            {// Body Rows
            filtered.map((item, itemIndex) => {
              const isVisible = rowIsVisible(itemIndex);
              if (!isVisible) {
                return (
                  <VirtualTr key={itemIndex} columnCount={visible.length} />
                );
              }

              return (
                <tr
                  key={item.id}
                  className={cx('row', {
                    highlight: highlight && highlight(item),
                  })}
                >
                  {// Body Cells
                  visible.map((column, columnIndex) => {
                    const isCheckbox = column.dataKey === CHECKBOX_COLUMN;
                    const checkboxProp = isCheckbox
                      ? { checked: selectedRows && selectedRows.has(item.id) }
                      : {};

                    return (
                      <Cell
                        offset={offsets[columnIndex]}
                        key={column.dataKey}
                        {...{ column, item, ...checkboxProp }}
                      />
                    );
                  }) /* Body Cells */}
                </tr>
              );
            }) /* Body Rows */}
          </tbody>
        </table>
      </div>
    );
  }
}

export default styled(Table)`
  position: relative;
  width: ${props => props.width};
  height: ${props => props.height};
  overflow: auto;
  table {
    border-collapse: collapse;
    border-spacing: 0;
  }
  table.table {
    min-width: 100%;
  }
  .hide-header thead > * {
    opacity: 0;
  }
  table.proxy-table {
    position: relative;
    min-width: 100%;
  }
  .proxy-table-wrapper {
    position: fixed;
    background: ${colors.grey10};
    z-index: 3;
    overflow: hidden;
  }
  .row.highlight {
    > td {
      background-color: ${colors.blue50};
    }
  }
  td {
    height: ${ROW_HEIGHT}px;
  }
`;
